import { useEffect, useState } from 'react';
import { ActivityIndicator, Alert, Linking, Platform, Pressable, Text, View, type ViewStyle } from 'react-native';
import type { AbaId } from '../store/types';
import { conferirCompras, setDoc } from '../store/store';
import { Btn } from './ui';
import { C, aba } from './theme';
import { cobrancaAtiva, PRECO_ABA_USD, PRECO_BASICO_USD, precoTexto } from '../lib/plano';
import { MODO_TESTE, appSemLoja, comprar, gerenciarUrl, lojaDisponivel, precosDaLoja, restaurar } from '../lib/compras';
import { moedaPadrao } from '../lib/moeda';
import { WEB_URL } from '../lib/config';
import { t } from '../lib/i18n';

/** Preço oficial da loja para as abas (some o valor aproximado assim que a loja responde). */
export function usePrecos(ids: string[]) {
  const [p, setP] = useState<Record<string, string>>({});
  const chave = ids.join(',');
  useEffect(() => { let vivo = true; precosDaLoja(ids).then(m => { if (vivo) setP(m); }); return () => { vivo = false; }; }, [chave]);
  return p;
}
export const precoAprox = (id: AbaId) => precoTexto(aba(id).gratis ? PRECO_BASICO_USD : PRECO_ABA_USD, moedaPadrao());

/** Botão de assinar uma aba: compra da Google Play / App Store, ou a simulação enquanto a loja não está ligada. */
export function Assinar({ id, cor, outline, style, preco }: { id: AbaId; cor: string; outline?: boolean; style?: ViewStyle; preco?: string }) {
  const [busy, setBusy] = useState(false);
  if (!cobrancaAtiva()) return null;
  const nome = aba(id).gratis ? t('Plano básico: My Planner + Anotações') : aba(id).nome;

  if (MODO_TESTE) return <Btn cor={cor} outline={outline} style={style} label={t('Assinar (simulação de teste)')}
    onPress={() => setDoc('ajustes', d => ({ ...d, assinaturas: { ...d.assinaturas, [id]: true } }))} />;

  if (lojaDisponivel()) {
    const assinar = async () => {
      setBusy(true);
      const r = await comprar(id);
      setBusy(false);
      if (r.ok) Alert.alert('🎉 ' + t('Assinatura feita'), t('{aba} liberada. Vale também nos seus outros dispositivos e no site.', { aba: nome }));
      else if (!r.cancelou) Alert.alert(t('Não deu para assinar agora'), r.erro === 'produto'
        ? t('Esta assinatura ainda não está disponível na loja. Tente de novo mais tarde.')
        : t('Confira a internet e tente de novo. Nada foi cobrado.'));
    };
    return busy ? <ActivityIndicator color={cor} style={[{ marginVertical: 14 }, style]} />
      : <Btn cor={cor} outline={outline} style={style} label={t('Assinar por {preco} por ano', { preco: preco || precoAprox(id) })} onPress={assinar} />;
  }

  if (appSemLoja()) return <Text style={[{ color: C.sub, fontSize: 13, textAlign: 'center' }, style]}>
    {t('Para assinar, atualize o My Planner pela loja de apps do celular.')}</Text>;

  // Site: a compra é no app; aqui só confere.
  return (
    <View style={[{ gap: 8 }, style]}>
      <Text style={{ color: C.sub, fontSize: 13, textAlign: 'center' }}>{t('A assinatura é feita no app do celular, pela loja de apps, e vale também aqui no site.')}</Text>
      <Btn cor={cor} outline label={busy ? t('Conferindo...') : t('Já assinei no celular: conferir')} disabled={busy}
        onPress={async () => { setBusy(true); await conferirCompras(); setBusy(false); }} />
    </View>
  );
}

/** Texto que as lojas pedem junto da compra: renovação, cancelamento, termos e privacidade. */
export function AvisoAssinatura() {
  if (!cobrancaAtiva() || MODO_TESTE) return null;
  const link = (l: string, url: string) => <Text style={{ color: C.sub, fontSize: 11, textDecorationLine: 'underline' }} onPress={() => Linking.openURL(url)}>{l}</Text>;
  return (
    <Text style={{ color: C.mute, fontSize: 11, textAlign: 'center' }}>
      {t('Assinatura anual, renova sozinha. Cancele quando quiser nas assinaturas da loja, até 24 horas antes da renovação.')}{' '}
      {link(t('Termos de uso'), `${WEB_URL}/termos`)} · {link(t('Privacidade'), `${WEB_URL}/privacidade`)}
    </Text>
  );
}

/** Ajustes > Compras: restaurar e gerenciar as assinaturas da loja. */
export function GerenciarAssinaturas({ cor }: { cor: string }) {
  const [busy, setBusy] = useState(false);
  if (!cobrancaAtiva() || MODO_TESTE) return null;
  const nativo = Platform.OS !== 'web';
  return (
    <View style={{ gap: 8 }}>
      {lojaDisponivel() && <Btn cor={cor} outline disabled={busy} label={busy ? t('Procurando...') : t('Restaurar compras')} onPress={async () => {
        setBusy(true); const n = await restaurar(); setBusy(false);
        Alert.alert(t('Restaurar compras'), n ? t('{n} assinatura(s) encontrada(s) e liberada(s).', { n }) : t('Nenhuma assinatura encontrada nesta conta da loja.'));
      }} />}
      {!nativo && <Btn cor={cor} outline disabled={busy} label={busy ? t('Conferindo...') : t('Conferir minhas assinaturas')}
        onPress={async () => { setBusy(true); await conferirCompras(); setBusy(false); }} />}
      <Pressable onPress={() => Linking.openURL(gerenciarUrl)}>
        <Text style={{ color: cor, textAlign: 'center', fontWeight: '700', paddingVertical: 6 }}>
          {nativo ? t('Gerenciar ou cancelar assinaturas na loja') : t('Gerenciar assinaturas (Google Play)')}</Text>
      </Pressable>
      {!nativo && <Pressable onPress={() => Linking.openURL('https://apps.apple.com/account/subscriptions')}>
        <Text style={{ color: cor, textAlign: 'center', fontWeight: '700', paddingVertical: 6 }}>{t('Gerenciar assinaturas (App Store)')}</Text>
      </Pressable>}
    </View>
  );
}
